import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, CircularProgress, Collapse, Snackbar, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StopIcon from "@mui/icons-material/Stop";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import DeleteConfirmModal from "../components/AlertComponents/DeleteConfirmModal";
import { STATUS_CONFIG, OUTCOME_CONFIG } from "../constants/campaignConstants";
import {
  createCampaignCheckout,
  deleteCampaign,
  fetchCampaign,
  fetchCampaignStats,
  buildEmailPreviewHtml,
  composeCampaignEmailPreview,
  resolveCampaignBillUrl,
  updateCampaign,
} from "../utils/campaignApi";
import { formatDateWithTimezone } from "../utils/campaignDateUtils";
import { buildCampaignQrDataUrl } from "../utils/campaignQrCode";
import { openCampaignPrintFlyer, CampaignPrintFlyerError } from "../utils/campaignPrintFlyer";
import CampaignPublishBlockersDialog from "../components/CampaignComponents/CampaignPublishBlockersDialog";
import { getPublishIssuesFromError } from "../utils/campaignPublishIssues";
import "../styles/component_styles/Campaigns.scss";

function formatBillType(type, number, congress) {
  const t = (type || "").toLowerCase();
  const num = number ? ` ${number}` : "";
  const congressSuffix = congress ? ` — ${congress}th Congress` : "";
  if (t === "hr") return `H.R.${num} (House)${congressSuffix}`;
  if (t === "s") return `S.${num} (Senate)${congressSuffix}`;
  if (t === "hjres") return `H.J.Res.${num} (House)${congressSuffix}`;
  if (t === "sjres") return `S.J.Res.${num} (Senate)${congressSuffix}`;
  if (t === "hconres") return `H.Con.Res.${num} (House)${congressSuffix}`;
  if (t === "sconres") return `S.Con.Res.${num} (Senate)${congressSuffix}`;
  return (type || "").toUpperCase() + num + congressSuffix;
}

export default function CampaignDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams();

  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrLargeDataUrl, setQrLargeDataUrl] = useState("");
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });
  const [activationPending, setActivationPending] = useState(false);
  const [paymentBlockersOpen, setPaymentBlockersOpen] = useState(false);
  const [paymentIssues, setPaymentIssues] = useState([]);

  const paymentStatus = useMemo(() => new URLSearchParams(location.search).get("payment"), [location.search]);

  const loadData = useCallback(async () => {
    try {
      const [campaignData, statsData] = await Promise.all([
        fetchCampaign(campaignId),
        fetchCampaignStats(campaignId).catch(() => null),
      ]);
      setCampaign(campaignData);
      setStats(statsData);
      return campaignData;
    } catch (error) {
      setSnack({ open: true, severity: "error", message: error?.response?.data?.error || "Failed to load campaign." });
      return null;
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh call/action counts while the organizer watches an active campaign.
  useEffect(() => {
    if (campaign?.status !== "active") return undefined;
    const intervalId = window.setInterval(() => {
      loadData();
    }, 20000);
    return () => window.clearInterval(intervalId);
  }, [campaign?.status, campaign?.id, loadData]);

  useEffect(() => {
    const flash = location.state?.flash;
    if (!flash) return;
    setSnack({ open: true, severity: "success", message: flash });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (paymentStatus === "cancelled") {
      setSnack({ open: true, severity: "info", message: "Payment was cancelled." });
      return;
    }
    if (paymentStatus !== "success") return;

    let cancelled = false;
    setActivationPending(true);
    setSnack({ open: true, severity: "success", message: "Payment received. Finalizing activation..." });

    const poll = async () => {
      for (let i = 0; i < 6; i += 1) {
        if (cancelled) return;
        const refreshed = await loadData();
        if (refreshed?.status === "active") {
          if (!cancelled) {
            setActivationPending(false);
            setSnack({ open: true, severity: "success", message: "Campaign is now active." });
          }
          return;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
      if (!cancelled) {
        setActivationPending(true);
        setSnack({
          open: true,
          severity: "warning",
          message:
            "Payment was received, but activation is taking longer than expected. Refresh below or contact support@civixthread.com if this continues.",
        });
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [paymentStatus, loadData]);

  const statusConfig = STATUS_CONFIG[campaign?.status] || STATUS_CONFIG.draft;
  const isVerified = Boolean(campaign?.verified_calling_enabled);
  const isDraftOrPending = isVerified && (campaign?.status === "draft" || campaign?.status === "pending_payment");
  const progressPercent = isVerified && campaign?.max_calls > 0
    ? Math.round((campaign.calls_used / campaign.max_calls) * 100)
    : 0;
  const staleStart = isDraftOrPending && campaign?.start_datetime && new Date(campaign.start_datetime) <= new Date();
  const campaignStatus = campaign?.status;
  const canStop = campaignStatus === "active" || campaignStatus === "paused";
  const canDelete = Boolean(
    campaignStatus === "draft"
    || campaignStatus === "pending_payment"
    || campaignStatus === "paused"
    || campaignStatus === "cancelled"
    || campaignStatus === "completed"
  );
  const canEditFree = !isVerified && campaignStatus === "active";
  const canContinueFreeDraft = !isVerified && campaignStatus === "draft";
  const canEditCampaignContent = isDraftOrPending || canEditFree || canContinueFreeDraft;
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/take-action/${campaignId}`
    : `/take-action/${campaignId}`;

  useEffect(() => {
    if (!campaignId) return undefined;
    let cancelled = false;
    const loadQr = async () => {
      try {
        const [small, large] = await Promise.all([
          buildCampaignQrDataUrl(shareUrl, 180),
          buildCampaignQrDataUrl(shareUrl, 400),
        ]);
        if (!cancelled) {
          setQrDataUrl(small);
          setQrLargeDataUrl(large);
        }
      } catch {
        if (!cancelled) {
          setQrDataUrl("");
          setQrLargeDataUrl("");
        }
      }
    };
    loadQr();
    return () => {
      cancelled = true;
    };
  }, [campaignId, shareUrl]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSnack({ open: true, severity: "success", message: "Take-action link copied." });
    } catch {
      setSnack({ open: true, severity: "error", message: "Could not copy link." });
    }
  };

  const handleShareLink = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: campaign?.name || "Take action",
          text: `Take action: ${campaign?.name || "this campaign"}`,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    await handleCopyLink();
  };

  const handlePrintFlyer = async () => {
    try {
      const billLabel =
        campaign.bill_mode === "external"
          ? campaign?.bill?.link_name || campaign?.bill?.link_url || ""
          : campaign.target_bill_type && campaign.target_bill_number
            ? formatBillType(
                campaign.target_bill_type,
                campaign.target_bill_number,
                campaign.target_bill_congress
              )
            : "";
      await openCampaignPrintFlyer({
        name: campaign.name,
        description: campaign.description,
        actionScript: campaign.action_script,
        shareUrl,
        billLabel,
        organizerName: campaign.organizer_name || "",
      });
    } catch (error) {
      let message = "Could not open the print flyer. Please try again.";
      if (error instanceof CampaignPrintFlyerError) {
        if (error.code === "popup_blocked") {
          message = error.message;
        } else if (error.code === "missing_share_url") {
          message = error.message;
        }
      } else if (error?.message) {
        message = error.message;
      }
      setSnack({
        open: true,
        severity: "error",
        message,
      });
    }
  };
  const handleCopyEmailDraft = async () => {
    const billLine = campaign.bill_mode === "external"
      ? (campaign?.bill?.link_name || "")
      : (campaign.target_bill_type && campaign.target_bill_number
          ? formatBillType(campaign.target_bill_type, campaign.target_bill_number, campaign.target_bill_congress)
          : "");
    const billUrl = resolveCampaignBillUrl({
      billMode: campaign.bill_mode,
      billDbId: campaign.target_bill_db_id,
      externalBillUrl: campaign?.bill?.link_url,
      origin: typeof window !== "undefined" ? window.location.origin : "",
    });
    const preview = composeCampaignEmailPreview({
      emailSubject: campaign.email_subject || `Take action: ${campaign.name}`,
      emailBody: campaign.email_body || "",
      name: campaign.name,
      description: campaign.description,
      actionScript: campaign.action_script,
      billLine,
      billUrl,
      relatedLinks: campaign.additional_links,
      targets: campaign.custom_targets || [],
      shareUrl,
    });
    const draft = `Subject: ${preview.subject}\n\n${preview.body}`;
    try {
      if (typeof window !== "undefined" && window.ClipboardItem && navigator.clipboard?.write) {
        const html = buildEmailPreviewHtml({ subject: preview.subject, body: preview.body, name: campaign?.name });
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/plain": new Blob([draft], { type: "text/plain" }),
            "text/html": new Blob([html], { type: "text/html" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(draft);
      }
      setSnack({ open: true, severity: "success", message: "Email draft copied." });
    } catch {
      try {
        await navigator.clipboard.writeText(draft);
        setSnack({ open: true, severity: "success", message: "Email draft copied." });
      } catch {
        setSnack({ open: true, severity: "error", message: "Could not copy email draft." });
      }
    }
  };

  const handleStop = async () => {
    setActionLoading(true);
    try {
      await updateCampaign(campaignId, { status: "cancelled" });
      setStopOpen(false);
      await loadData();
      setSnack({ open: true, severity: "success", message: "Campaign stopped." });
    } catch (error) {
      setSnack({ open: true, severity: "error", message: error?.response?.data?.error || "Failed to stop campaign." });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await deleteCampaign(campaignId);
      navigate("/campaigns", {
        state: { flash: "Campaign deleted.", flashSeverity: "success" },
      });
    } catch (error) {
      setSnack({ open: true, severity: "error", message: error?.response?.data?.error || "Failed to delete campaign." });
      setActionLoading(false);
    }
  };

  const handlePayment = async () => {
    setActionLoading(true);
    try {
      const response = await createCampaignCheckout(campaignId);
      if (!response?.checkout_url) {
        throw new Error("Checkout URL was not returned.");
      }
      window.location.href = response.checkout_url;
    } catch (error) {
      const issues = getPublishIssuesFromError(error);
      if (issues && issues.length > 0) {
        setPaymentIssues(issues);
        setPaymentBlockersOpen(true);
      } else {
        setSnack({
          open: true,
          severity: "error",
          message: error?.response?.data?.error || error.message || "Failed to start checkout.",
        });
      }
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="campaign-detail">
        <div className="campaign-loading">
          <CircularProgress size={24} />
          <p style={{ marginTop: 10 }}>Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="campaign-detail">
        <div className="campaign-error">
          <h3>Campaign not found</h3>
          <p>We couldn&apos;t load this campaign.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-detail">
      <div className="campaign-detail__status">
        <span className="campaign-badge" style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}>
          {statusConfig.label}
        </span>
      </div>

      <h1 className="campaign-detail__title">{campaign.name}</h1>
      {campaign.description ? <p className="campaign-detail__description">{campaign.description}</p> : null}

      {isDraftOrPending ? (
        <p className="campaign-detail__hint">
          Review your setup below, edit anything that needs changing, then complete payment to go live.
        </p>
      ) : null}
      {canContinueFreeDraft ? (
        <p className="campaign-detail__hint">
          This campaign is still a draft. Continue setup and publish when you are ready to share the take-action link.
        </p>
      ) : null}
      {!isVerified && campaign.status === "active" ? (
        <p className="campaign-detail__hint">
          This campaign is free. Share the take-action link so people can call with your script.
          {campaign.schedule_locked
            ? " Now that it is running, the start time is locked — you can still extend the end date."
            : ""}
        </p>
      ) : null}
      {!isVerified && campaignStatus === "paused" ? (
        <p className="campaign-detail__hint">
          This campaign is paused. Stop it to close the take-action page, or delete it.
        </p>
      ) : null}
      {campaign.status === "completed" ? (
        <p className="campaign-detail__hint">
          This campaign has ended. The take-action page no longer accepts new actions.
        </p>
      ) : null}
      {campaignStatus === "cancelled" ? (
        <p className="campaign-detail__hint">
          This campaign was stopped and is no longer accepting actions. You can delete it to remove it from your dashboard.
        </p>
      ) : null}
      {staleStart ? (
        <p className="campaign-detail__warning">
          Your campaign start date has passed. Please edit the schedule before paying.
        </p>
      ) : null}

      <div className="campaign-detail__stats-grid">
        {isVerified ? (
          isDraftOrPending ? (
            <>
              <div className="campaign-detail__stat">
                <p className="campaign-card__stat-label">Max Calls</p>
                <p className="campaign-card__stat-value">{campaign.max_calls}</p>
              </div>
              <div className="campaign-detail__stat">
                <p className="campaign-card__stat-label">Cost Per Call</p>
                <p className="campaign-card__stat-value">${campaign.cost_per_call.toFixed(2)}</p>
              </div>
              <div className="campaign-detail__stat">
                <p className="campaign-card__stat-label">Total Budget</p>
                <p className="campaign-card__stat-value">${campaign.total_cost.toFixed(2)}</p>
              </div>
            </>
          ) : (
            <>
              <div className="campaign-detail__stat">
                <p className="campaign-card__stat-label">Calls Made</p>
                <p className="campaign-card__stat-value">{campaign.calls_used}</p>
              </div>
              <div className="campaign-detail__stat">
                <p className="campaign-card__stat-label">Remaining</p>
                <p className="campaign-card__stat-value">{campaign.remaining_calls ?? "—"}</p>
              </div>
              <div className="campaign-detail__stat">
                <p className="campaign-card__stat-label">Budget</p>
                <p className="campaign-card__stat-value">${campaign.total_cost.toFixed(2)}</p>
              </div>
            </>
          )
        ) : (
          <>
            <div className="campaign-detail__stat">
              <p className="campaign-card__stat-label">Total calls</p>
              <p className="campaign-card__stat-value">{campaign.call_completions ?? campaign.action_completions ?? 0}</p>
            </div>
            <div className="campaign-detail__stat">
              <p className="campaign-card__stat-label">Verified calls</p>
              <p className="campaign-card__stat-value">{campaign.call_completions_verified || 0}</p>
            </div>
            <div className="campaign-detail__stat">
              <p className="campaign-card__stat-label">Total emails</p>
              <p className="campaign-card__stat-value">{campaign.email_completions || 0}</p>
            </div>
            <div className="campaign-detail__stat">
              <p className="campaign-card__stat-label">Verified emails</p>
              <p className="campaign-card__stat-value">{campaign.email_completions_verified || 0}</p>
            </div>
            <div className="campaign-detail__stat">
              <p className="campaign-card__stat-label">Type</p>
              <p className="campaign-card__stat-value">Free</p>
            </div>
            <div className="campaign-detail__stat">
              <p className="campaign-card__stat-label">Rep source</p>
              <p className="campaign-card__stat-value">
                {campaign.rep_source === "viewer_reps" ? "Viewer reps" : "Custom list"}
              </p>
            </div>
          </>
        )}
      </div>

      {isVerified && !isDraftOrPending ? (
        <div className="campaign-progress">
          <div className="campaign-progress__bar">
            <div
              className="campaign-progress__fill"
              style={{
                width: `${Math.min(100, Math.max(0, progressPercent))}%`,
                backgroundColor: progressPercent >= 90 ? "#dc2626" : "#047857",
              }}
            />
          </div>
          <div className="campaign-progress__text">{progressPercent}% used</div>
        </div>
      ) : null}

      {isDraftOrPending ? (
        <p className="campaign-detail__warning">
          Supporters cannot use this link until you complete payment and the campaign is active.
        </p>
      ) : null}
      <div className="campaign-share-link">
        <span className="campaign-share-link__label">Take-action link</span>
        <a
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="campaign-share-link__url"
        >
          {shareUrl}
        </a>
      </div>
      <div className="campaign-share-actions">
        <button
          type="button"
          className="campaign-btn campaign-btn--primary campaign-share-actions__print"
          onClick={handlePrintFlyer}
        >
          Print flyer
        </button>
        <button
          type="button"
          className="campaign-btn"
          onClick={handleCopyLink}
        >
          Copy link
        </button>
        <button
          type="button"
          className="campaign-btn"
          onClick={handleShareLink}
        >
          Send link
        </button>
      </div>
      <p className="campaign-share-actions__hint">
        Print a one-page flyer with a large QR code. People scan it to open this take-action page and call.
      </p>
      <div className="campaign-share-qr">
        <button
          type="button"
          className="campaign-share-qr__btn"
          onClick={() => setQrModalOpen(true)}
          title="Enlarge QR code"
          disabled={!qrDataUrl}
        >
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="QR code for take-action campaign"
              width={180}
              height={180}
            />
          ) : (
            <div className="campaign-share-qr__placeholder" aria-hidden="true">
              <CircularProgress size={28} sx={{ color: "#047857" }} />
            </div>
          )}
          <span className="campaign-share-qr__hint">Click to enlarge</span>
        </button>
      </div>

      {(campaign.email_subject || campaign.email_body) ? (
        <>
          <div className="campaign-divider" />
          <div className="campaign-email-actions">
            <button
              type="button"
              className="campaign-btn"
              onClick={handleCopyEmailDraft}
            >
              Copy email
            </button>
            {canEditCampaignContent ? (
            <button
              type="button"
              className="campaign-btn"
              onClick={() => navigate(`/campaigns/${campaign.id}/edit?step=preview`)}
            >
              View email
            </button>
            ) : null}
          </div>
        </>
      ) : null}

      <h2 className="campaign-section-title campaign-section-title--spaced">Campaign Details</h2>
      {campaign.target_bill_type && campaign.target_bill_number ? (
        <div className="campaign-detail__row">
          <span className="campaign-detail__label">Target Bill</span>
          <span className="campaign-detail__value">
            {campaign.target_bill_db_id ? (
              <Link to={`/bills/${campaign.target_bill_db_id}`} style={{ color: "#0f172a", textDecoration: "underline" }}>
                {formatBillType(campaign.target_bill_type, campaign.target_bill_number, campaign.target_bill_congress)}
              </Link>
            ) : (
              formatBillType(campaign.target_bill_type, campaign.target_bill_number, campaign.target_bill_congress)
            )}
          </span>
        </div>
      ) : null}
      {campaign.bill_mode === "external" && campaign?.bill?.link_url ? (
        <div className="campaign-detail__row">
          <span className="campaign-detail__label">External bill</span>
          <span className="campaign-detail__value">
            <a href={campaign.bill.link_url} target="_blank" rel="noreferrer" style={{ color: "#0f172a", textDecoration: "underline" }}>
              {campaign.bill.link_name || campaign.bill.link_url}
            </a>
          </span>
        </div>
      ) : null}
      {Array.isArray(campaign.additional_links) && campaign.additional_links.length > 0 ? (
        <div className="campaign-detail__row">
          <span className="campaign-detail__label">Related links</span>
          <span className="campaign-detail__value">
            {campaign.additional_links.map((link) => (
              <div key={`${link.name}-${link.url}`}>
                <a href={link.url} target="_blank" rel="noreferrer" style={{ color: "#0f172a", textDecoration: "underline" }}>
                  {link.name}
                </a>
              </div>
            ))}
          </span>
        </div>
      ) : null}
      <div className="campaign-detail__row">
        <span className="campaign-detail__label">Start Date</span>
        <span className="campaign-detail__value">{formatDateWithTimezone(campaign.start_datetime, campaign.timezone)}</span>
      </div>
      <div className="campaign-detail__row">
        <span className="campaign-detail__label">End Date</span>
        <span className="campaign-detail__value">{formatDateWithTimezone(campaign.end_datetime, campaign.timezone)}</span>
      </div>

      {stats?.outcome_breakdown && Object.keys(stats.outcome_breakdown).length > 0 ? (
        <>
          <div className="campaign-divider" />
          <h2 className="campaign-section-title">Call Outcomes</h2>
          <div className="campaign-outcomes">
            {Object.entries(stats.outcome_breakdown).map(([key, count]) => {
              const cfg = OUTCOME_CONFIG[key] || { label: key, color: "#334155" };
              return (
                <div className="campaign-detail__row" key={key}>
                  <span className="campaign-detail__label" style={{ color: cfg.color }}>{cfg.label}</span>
                  <span className="campaign-detail__value">{count}</span>
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {isVerified && !isDraftOrPending ? (
        <>
          <button
            type="button"
            className="campaign-btn"
            onClick={() => setExpanded((v) => !v)}
            style={{ marginBottom: 10 }}
          >
            {expanded ? "Hide" : "Show"} How Verified Calls Work
          </button>
          <Collapse in={expanded}>
            <div className="campaign-detail__hint" style={{ marginBottom: 8 }}>
              CivixThread verifies caller flow and outcomes for your campaign&apos;s targeted bill so your team can monitor engagement quality, not just click metrics.
            </div>
          </Collapse>
        </>
      ) : null}

      <div className="campaign-actions">
        {canContinueFreeDraft ? (
          <>
            <button type="button" className="campaign-btn campaign-btn--danger" onClick={() => setDeleteOpen(true)} disabled={actionLoading}>
              Delete
            </button>
            <button type="button" className="campaign-btn campaign-btn--icon campaign-btn--edit" onClick={() => navigate(`/campaigns/${campaign.id}/edit`)} disabled={actionLoading}>
              <EditOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
              Continue setup
            </button>
          </>
        ) : null}

        {isDraftOrPending ? (
          <>
            <button type="button" className="campaign-btn campaign-btn--danger" onClick={() => setDeleteOpen(true)} disabled={actionLoading}>
              Delete
            </button>
            <button type="button" className="campaign-btn campaign-btn--icon campaign-btn--edit" onClick={() => navigate(`/campaigns/${campaign.id}/edit`)} disabled={actionLoading}>
              <EditOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
              Edit
            </button>
            <button
              type="button"
              className="campaign-btn campaign-btn--primary"
              onClick={handlePayment}
              disabled={actionLoading || staleStart}
            >
              {actionLoading ? "Processing..." : `Complete Payment ($${campaign.total_cost.toFixed(2)})`}
            </button>
          </>
        ) : null}

        {!isDraftOrPending && canDelete ? (
          <button type="button" className="campaign-btn campaign-btn--danger" onClick={() => setDeleteOpen(true)} disabled={actionLoading}>
            Delete
          </button>
        ) : null}

        {canEditFree ? (
          <button type="button" className="campaign-btn campaign-btn--icon campaign-btn--edit" onClick={() => navigate(`/campaigns/${campaign.id}/edit`)} disabled={actionLoading}>
            <EditOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
            Edit
          </button>
        ) : null}

        {canStop ? (
          <button type="button" className="campaign-btn campaign-btn--stop campaign-btn--icon" onClick={() => setStopOpen(true)} disabled={actionLoading}>
            <StopIcon sx={{ fontSize: 18 }} aria-hidden />
            {actionLoading ? "Stopping..." : "Stop Campaign"}
          </button>
        ) : null}
      </div>

      {activationPending ? (
        <div className="campaign-alert">
          <Alert
            severity="warning"
            action={(
              <Button color="inherit" size="small" onClick={() => loadData()}>
                Refresh status
              </Button>
            )}
          >
            Payment was received, but activation is taking longer than expected. Try refreshing.
            If this continues, email{" "}
            <a href="mailto:support@civixthread.com">support@civixthread.com</a>.
          </Alert>
        </div>
      ) : null}

      <DeleteConfirmModal
        open={stopOpen}
        onClose={() => setStopOpen(false)}
        onConfirm={handleStop}
        loading={actionLoading}
        title="Stop Campaign"
        description="Stop this campaign? Supporters will no longer be able to take action. This cannot be undone. You can delete it afterward."
        confirmLabel="Stop Campaign"
      />

      <DeleteConfirmModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        loading={actionLoading}
        title="Delete Campaign"
        description={
          isVerified || (campaign?.amount_paid ?? 0) > 0
            ? "This will permanently delete the campaign and remove it from your dashboard, including local payment and call stats. This does not issue a refund."
            : "Are you sure you want to delete this campaign? This action cannot be undone."
        }
        confirmLabel="Delete"
      />

      <CampaignPublishBlockersDialog
        open={paymentBlockersOpen}
        onClose={() => setPaymentBlockersOpen(false)}
        issues={paymentIssues}
        title="Complete setup before payment"
        onGoToStep={(step) => {
          navigate(`/campaigns/${campaignId}/edit?step=${step}`);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: "12px", padding: "16px", textAlign: "center" }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, pb: 1, mb: 1, fontSize: "20px", fontWeight: 800, color: "#0f172a" }}>
          Scan to take action
          <IconButton
            aria-label="close"
            onClick={() => setQrModalOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#64748b",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <img
            src={qrLargeDataUrl || qrDataUrl || undefined}
            alt="Enlarged QR code"
            style={{ display: "block", margin: "0 auto", width: "100%", maxWidth: "400px", height: "auto", borderRadius: "8px", border: "1px solid #e2e8f0" }}
          />
          <p style={{ marginTop: "16px", marginBottom: 0, fontSize: "15px", color: "#0f172a", lineHeight: 1.4 }}>
            Let people scan this from your screen so they can call right away.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
